import { Dto } from '@damofire/data-commons';
import { InactivityWarnings } from '@damofire/data-security';
import { OrganizationInfos } from '@damofire/data-stas';
import { Timestamp } from 'firebase/firestore';
import { InspectionsStatistics, IssuesStatistics } from '../asset/asset.dto';

export interface WarehouseStatistics {
  assets?: number;
  issues?: IssuesStatistics;
  inspections?: InspectionsStatistics;
}

export interface WarehouseSectionBounds {
  name: string;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
  color?: string;
  borderColor?: string;
  systems?: number[];
  aisles?: string[];
  angle?: number;
  cx?: number;
  cy?: number;
}

export interface SyncedWarehouse extends Warehouse {
  lastAccessedDate?: Date;
}

export interface Warehouse extends OrganizationInfos {
  name?: string;
  deleteMe?: boolean;
  enabled?: boolean;
  hasDrawing?: boolean;
  hasCertificationInfos?: boolean;
  import?: WarehouseImportStatus;
  toUpdate?: boolean;
  statistics?: WarehouseStatistics;
  extras?: Record<string, any>;
  sections?: string[];
  sectionBounds?: WarehouseSectionBounds[];
  sectionMode?: boolean;
  canEditFromLayout?: boolean;
  inactivityStatus?: InactivityWarnings;
  aiAgentEnabled?: boolean;
  showSystemNumbers?: boolean;
  systemNumberOrientation?: SystemNumberOrientation;
}

export enum SystemNumberOrientation {
  DOWN_AISLE = 'DOWN_AISLE',
  ALWAYS_UP = 'ALWAYS_UP',
}

export interface WarehouseImportStatus extends Dto<string> {
  status?: string;
}

export interface Import {
  _id?: string;
  warehouseId?: number;
  status?: 'STARTED' | 'COMPLETED' | 'FAILED';
  step?: string;
  steps?: string[];
  endTime?: Timestamp;
  bundleValidationStep?: ImportStep;
  picturesExtractionStep?: ImportStep;
  csvWarehousesParserStep?: ImportStep;
  csvSystemParserStep?: ImportStep;
  csvAssetsParserStep?: ImportStep;
  csvIssuesParserStep?: ImportStep;
  csvPicturesParserStep?: ImportStep;
  params?: ImportParams;
  lastUpdated?: Date;
}

export interface ImportParams {
  organization: string;
  division: string;
  divisionName: string;
  name: string;
  reviewer: string;
  bundleName: string;
  pubsubTopic: string;
  insertIntoDb: boolean;
  certification: boolean;
  layoutOnly: boolean;
  reimport: boolean;
  canMatchData: boolean;
  messageId: string;
  user: string;
  analysisImportId?: string;
}

export interface ImportStep {
  status?: 'STARTED' | 'COMPLETED' | 'FAILED';
  startTime?: Timestamp | Date;
  endTime?: Timestamp | Date;
  result?: ImportStepResult;
  error?: string;
}

export interface ImportStepResult {
  processSkipCount?: number;
  readCount?: number;
  writeCount?: number;
  ignore?: boolean;
  reasons?: Record<string, string[]>;
  reasonGroups?: ImportReasonGroup[];
}

export interface ImportReasonGroup {
  code: string;
  count: number;
  items: string[];
}

export interface StatItem {
  key: string;
  doc_count: number;
}

export interface WarehouseOutlineSegment extends Dto<string> {
  layer?: string;
  vertices?: { x: number; y: number }[];
  divisionId?: string;
}

export interface LayoutTextSegment extends Dto<string> {
  divisionId?: string;
  text?: string;
  xOrigin?: number;
  yOrigin?: number;
  rotation?: number; // degrees
  fontHeight?: number;
  horizontalAlign?: 'left' | 'center' | 'right';
  verticalAlign?: VerticalAlign;
}

export enum VerticalAlign {
  top = 'top',
  middle = 'middle',
  baseline = 'baseline',
  bottom = 'bottom',
}
